import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { getMemberAccessToken, supabase } from '../auth/supabase';
import './admin.css';
import './v12-admin.css';

const REMS_SHARED_EMAIL = '24rem@metrofire.ca.gov';
const EMPTY_BURN = { name: '', burnDate: '', startTime: '', endTime: '', location: '', staging: '', notes: '' };

function activeLineMedic(member) {
  return member?.approved && !member?.disabled && String(member?.member_kind || 'line_medic') === 'line_medic' && String(member?.email || '').trim().toLowerCase() !== REMS_SHARED_EMAIL;
}

function when(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function statusLabel(value) {
  return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

async function dispatchQueued(limit) {
  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');
  const response = await fetch('/api/notifications', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'dispatch_queued', limit: Math.max(1, Math.min(100, Number(limit) || 40)) }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || 'Unable to dispatch queued notifications.');
  return payload;
}

export default function PrescribedBurnsAdmin({ profile }) {
  const [burnForm, setBurnForm] = useState(EMPTY_BURN);
  const [requirementsForm, setRequirementsForm] = useState([{ position: 'Line Medic', quantity: 1 }]);
  const [editingBurnId, setEditingBurnId] = useState('');
  const [sendOperationalSms, setSendOperationalSms] = useState(false);
  const [burns, setBurns] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [signups, setSignups] = useState([]);
  const [history, setHistory] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedBurnId, setSelectedBurnId] = useState('');
  const [rosterDraft, setRosterDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [warning, setWarning] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!profile?.is_admin) return;
    setError('');
    const [burnResult, reqResult, signupResult, historyResult, memberResult] = await Promise.all([
      supabase.from('prescribed_burns').select('*').order('burn_date', { ascending: false }),
      supabase.from('prescribed_burn_requirements').select('*').order('sort_order', { ascending: true }),
      supabase.from('prescribed_burn_signups').select('*').order('signed_up_at', { ascending: true }),
      supabase.from('prescribed_burn_history').select('*').order('created_at', { ascending: false }),
      supabase.rpc('admin_list_notification_members'),
    ]);
    const firstError = burnResult.error || reqResult.error || signupResult.error || historyResult.error || memberResult.error;
    if (firstError) {
      setError(firstError.message || 'Unable to load prescribed burn administration.');
      setStatus('error');
      return;
    }
    setBurns(Array.isArray(burnResult.data) ? burnResult.data : []);
    setRequirements(Array.isArray(reqResult.data) ? reqResult.data : []);
    setSignups(Array.isArray(signupResult.data) ? signupResult.data : []);
    setHistory(Array.isArray(historyResult.data) ? historyResult.data : []);
    setMembers((Array.isArray(memberResult.data) ? memberResult.data : []).filter(activeLineMedic));
    setStatus('ready');
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const selectedBurn = useMemo(() => burns.find(item => item.id === selectedBurnId) || null, [burns, selectedBurnId]);
  const selectedSignups = useMemo(() => signups.filter(item => item.burn_id === selectedBurnId), [signups, selectedBurnId]);
  const selectedRequirements = useMemo(() => requirements.filter(item => item.burn_id === selectedBurnId), [requirements, selectedBurnId]);
  const selectedHistory = useMemo(() => history.filter(item => item.burn_id === selectedBurnId), [history, selectedBurnId]);
  const memberFor = id => members.find(member => member.id === id) || null;

  useEffect(() => {
    const next = {};
    selectedSignups.forEach(signup => {
      next[signup.id] = {
        selected: signup.status === 'selected',
        standby: signup.status === 'standby',
        position: signup.selected_position || '',
      };
    });
    setRosterDraft(next);
  }, [selectedBurnId, selectedSignups]);

  const updateRequirement = (index, key, value) => setRequirementsForm(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: key === 'quantity' ? Math.max(1, Number(value) || 1) : value } : item));

  const resetEditor = () => {
    setBurnForm(EMPTY_BURN);
    setRequirementsForm([{ position: 'Line Medic', quantity: 1 }]);
    setEditingBurnId('');
    setSendOperationalSms(false);
  };

  const startEdit = burn => {
    const reqs = requirements.filter(item => item.burn_id === burn.id);
    setEditingBurnId(burn.id);
    setSelectedBurnId(burn.id);
    setBurnForm({
      name: burn.name || '',
      burnDate: burn.burn_date || '',
      startTime: String(burn.start_time || '').slice(0, 5),
      endTime: String(burn.end_time || '').slice(0, 5),
      location: burn.location || '',
      staging: burn.staging_instructions || '',
      notes: burn.notes || '',
    });
    setRequirementsForm(reqs.length ? reqs.map(item => ({ position: item.position, quantity: item.quantity })) : [{ position: 'Line Medic', quantity: 1 }]);
    setMessage('');
    setWarning('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const queueBurnNotifications = async (burn, memberIds, eventType, { sms = false, stamp = '' } = {}) => {
    const targetMembers = unique(memberIds).map(id => memberFor(id)).filter(activeLineMedic);
    if (!targetMembers.length) return { queued: 0, smsSkipped: 0, dispatch: null };

    const { data, error: queueError } = await supabase.rpc('admin_queue_operational_notifications', {
      target_source_type: 'prescribed_burn',
      target_source_id: burn.id,
      target_event_type: eventType,
      target_member_ids: targetMembers.map(member => member.id),
      include_push: true,
      include_sms: sms,
      target_deep_link: `/prescribed-burns/${burn.id}`,
      target_metadata: { burn_status: burn.status },
      target_stamp: stamp || `${burn.status || 'state'}-${burn.updated_at || 'v1'}`,
    });
    if (queueError) throw queueError;

    const queued = Number(data?.queued || 0);
    const dispatch = queued > 0 ? await dispatchQueued(Math.min(100, Math.max(queued + 10, 30))) : null;
    return { queued, smsSkipped: Number(data?.sms_skipped || 0), dispatch };
  };

  const recordHistory = async (burnId, eventType, details = {}, memberId = null) => {
    const { error: historyError } = await supabase.from('prescribed_burn_history').insert({
      burn_id: burnId,
      member_id: memberId,
      actor_user_id: profile.id,
      event_type: eventType,
      details,
    });
    if (historyError) throw historyError;
  };

  const saveBurn = async openAfterSave => {
    setError('');
    setMessage('');
    setWarning('');
    if (!burnForm.name.trim() || !burnForm.burnDate) return setError('Enter the burn name and date.');
    if (!requirementsForm.some(item => item.position.trim())) return setError('Add at least one staffing requirement.');
    setSaving(true);
    let savedBurn = null;
    try {
      const now = new Date().toISOString();
      const payload = {
        name: burnForm.name.trim(),
        burn_date: burnForm.burnDate,
        start_time: burnForm.startTime || null,
        end_time: burnForm.endTime || null,
        location: burnForm.location.trim(),
        staging_instructions: burnForm.staging.trim(),
        notes: burnForm.notes.trim(),
        updated_at: now,
      };

      if (editingBurnId) {
        const existing = burns.find(item => item.id === editingBurnId);
        if (!existing) throw new Error('The prescribed burn is no longer available.');
        const { data, error: updateError } = await supabase.from('prescribed_burns').update(payload).eq('id', editingBurnId).select('*').single();
        if (updateError) throw updateError;
        savedBurn = data;
        const { error: deleteReqError } = await supabase.from('prescribed_burn_requirements').delete().eq('burn_id', editingBurnId);
        if (deleteReqError) throw deleteReqError;
      } else {
        const { data, error: burnError } = await supabase.from('prescribed_burns').insert({
          ...payload,
          status: 'draft',
          created_by: profile.id,
        }).select('*').single();
        if (burnError) throw burnError;
        savedBurn = data;
      }

      const requirementRows = requirementsForm.filter(item => item.position.trim()).map((item, index) => ({ burn_id: savedBurn.id, position: item.position.trim(), quantity: Math.max(1, Number(item.quantity) || 1), sort_order: index }));
      const { error: requirementError } = await supabase.from('prescribed_burn_requirements').insert(requirementRows);
      if (requirementError) throw requirementError;
      await recordHistory(savedBurn.id, editingBurnId ? 'burn_details_updated' : 'draft_created', { requirements: requirementRows.map(item => ({ position: item.position, quantity: item.quantity })) });

      let notifyWarning = '';
      if (editingBurnId && savedBurn.status !== 'draft') {
        try {
          const audienceIds = savedBurn.status === 'roster_selected'
            ? signups.filter(item => item.burn_id === savedBurn.id && item.status === 'selected').map(item => item.member_id)
            : members.map(member => member.id);
          const result = await queueBurnNotifications(savedBurn, audienceIds, 'prescribed_burn_updated', { sms: sendOperationalSms, stamp: now });
          if (result.smsSkipped) notifyWarning = `${result.smsSkipped} member${result.smsSkipped === 1 ? '' : 's'} did not opt in to operational SMS and were not texted.`;
          if (result.dispatch?.failed) notifyWarning = `${notifyWarning ? `${notifyWarning} ` : ''}${result.dispatch.failed} notification provider send${result.dispatch.failed === 1 ? '' : 's'} failed; the burn changes remain saved.`;
        } catch (notifyError) {
          notifyWarning = `Burn changes were saved, but notification processing needs attention: ${notifyError?.message || 'notification dispatch failed.'}`;
        }
      }

      resetEditor();
      await load();
      setSelectedBurnId(savedBurn.id);

      if (!editingBurnId && openAfterSave) {
        await transitionBurn(savedBurn, 'open_signup', { skipLoad: false });
        return;
      }

      setMessage(editingBurnId ? 'Prescribed burn details updated.' : 'Draft saved. Use Open Signup when the event is ready for members.');
      setWarning(notifyWarning);
    } catch (saveError) {
      setError(saveError?.message || 'Unable to save the prescribed burn.');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const transitionBurn = async (burn, action, { skipLoad = false } = {}) => {
    setError('');
    setMessage('');
    setWarning('');
    const confirmText = {
      close_signup: `Close signup for “${burn.name}”? Members will no longer be able to volunteer or withdraw.`,
      mark_completed: `Mark “${burn.name}” completed? The member roster becomes read-only.`,
      cancel: `Cancel “${burn.name}”? Members with an active signup/selection will be notified.`,
      archive: `Archive “${burn.name}”? It will leave the active member view but remain in Admin history.`,
    }[action];
    if (confirmText && !window.confirm(confirmText)) return;

    setSaving(true);
    try {
      const { data: changed, error: transitionError } = await supabase.rpc('admin_transition_prescribed_burn', { target_burn_id: burn.id, action });
      if (transitionError) throw transitionError;

      if (action === 'open_signup') {
        try {
          const result = await queueBurnNotifications(changed, members.map(member => member.id), 'prescribed_burn_opened', { sms: sendOperationalSms, stamp: changed.updated_at });
          setMessage('Signup opened. Eligible Line Medics were queued for push notification.');
          if (result.smsSkipped) setWarning(`${result.smsSkipped} member${result.smsSkipped === 1 ? '' : 's'} did not opt in to operational SMS and were not texted.`);
        } catch (notifyError) {
          setMessage('Signup is open.');
          setWarning(`The event is saved, but notification processing needs attention: ${notifyError?.message || 'notification dispatch failed.'}`);
        }
      } else if (action === 'close_signup') {
        setMessage('Signup closed. Build the roster below, assign positions, then finalize it.');
      } else if (action === 'mark_completed') {
        setMessage('Prescribed burn marked completed. Member attendance is now read-only.');
      } else if (action === 'archive') {
        setMessage('Prescribed burn archived.');
      } else if (action === 'cancel') {
        const affected = unique(signups.filter(item => item.burn_id === burn.id && ['signed_up','selected','standby'].includes(item.status)).map(item => item.member_id));
        try {
          const result = await queueBurnNotifications(changed, affected, 'prescribed_burn_cancelled', { sms: sendOperationalSms, stamp: changed.updated_at });
          setMessage('Prescribed burn cancelled. Affected members were queued for notification.');
          if (result.smsSkipped) setWarning(`${result.smsSkipped} affected member${result.smsSkipped === 1 ? '' : 's'} did not opt in to operational SMS and were not texted.`);
        } catch (notifyError) {
          setMessage('Prescribed burn cancelled.');
          setWarning(`The cancellation is saved, but notification processing needs attention: ${notifyError?.message || 'notification dispatch failed.'}`);
        }
      }
      if (!skipLoad) await load();
      setSelectedBurnId(burn.id);
    } catch (transitionError) {
      setError(transitionError?.message || 'Unable to change the prescribed burn lifecycle.');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const setRosterChoice = (signupId, patch) => {
    setRosterDraft(current => {
      const previous = current[signupId] || { selected: false, standby: false, position: '' };
      const next = { ...previous, ...patch };
      if (patch.selected === true) next.standby = false;
      if (patch.standby === true) { next.selected = false; next.position = ''; }
      if (patch.selected === false) next.position = '';
      return { ...current, [signupId]: next };
    });
  };

  const candidateSignups = useMemo(() => selectedSignups.filter(item => item.status !== 'withdrawn' && item.status !== 'declined' && item.status !== 'deployed'), [selectedSignups]);
  const selectedDraftRows = useMemo(() => candidateSignups.filter(signup => rosterDraft[signup.id]?.selected), [candidateSignups, rosterDraft]);
  const staffingCounts = useMemo(() => {
    const counts = new Map();
    selectedDraftRows.forEach(signup => {
      const position = rosterDraft[signup.id]?.position || '';
      if (position) counts.set(position, (counts.get(position) || 0) + 1);
    });
    return counts;
  }, [selectedDraftRows, rosterDraft]);

  const saveRosterChanges = async ({ finalizing = false } = {}) => {
    if (!selectedBurn || selectedBurn.status !== 'signup_closed') throw new Error('Close signup before building the roster.');
    const allowedPositions = new Set(selectedRequirements.map(item => item.position));
    const missing = selectedDraftRows.find(signup => !String(rosterDraft[signup.id]?.position || '').trim());
    if (missing) throw new Error(`Assign a position to ${memberFor(missing.member_id)?.full_name || 'every selected member'} before saving.`);
    const invalid = selectedDraftRows.find(signup => !allowedPositions.has(rosterDraft[signup.id]?.position));
    if (invalid) throw new Error('A selected member has a position that is no longer part of this burn staffing plan.');

    const changes = [];
    for (const signup of candidateSignups) {
      const draft = rosterDraft[signup.id] || { selected: false, standby: false, position: '' };
      const nextStatus = draft.selected ? 'selected' : draft.standby ? 'standby' : 'not_selected';
      const nextPosition = draft.selected ? draft.position : null;
      if (signup.status === nextStatus && (signup.selected_position || null) === nextPosition) continue;
      const { error: updateError } = await supabase.from('prescribed_burn_signups').update({ status: nextStatus, selected_position: nextPosition, updated_at: new Date().toISOString() }).eq('id', signup.id);
      if (updateError) throw updateError;
      changes.push({ memberId: signup.member_id, from: signup.status, to: nextStatus, position: nextPosition });
    }

    if (changes.length) {
      await recordHistory(selectedBurn.id, 'admin_roster_saved', { changes });
    }
    if (!finalizing) {
      setMessage(`Roster saved. ${selectedDraftRows.length} member${selectedDraftRows.length === 1 ? '' : 's'} selected.`);
      await load();
    }
    return selectedDraftRows.map(signup => signup.member_id);
  };

  const saveRoster = async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      await saveRosterChanges();
    } catch (saveError) {
      setError(saveError?.message || 'Unable to save the roster.');
    } finally {
      setSaving(false);
    }
  };

  const finalizeRoster = async () => {
    if (!selectedBurn || !window.confirm(`Finalize the roster for “${selectedBurn.name}”? Selected assignments will be locked into the operational roster and members will be notified.`)) return;
    setSaving(true); setError(''); setMessage(''); setWarning('');
    try {
      const selectedMemberIds = await saveRosterChanges({ finalizing: true });
      if (!selectedMemberIds.length) throw new Error('Select at least one member before finalizing the roster.');
      const { data: finalized, error: finalizeError } = await supabase.rpc('admin_finalize_prescribed_burn_roster', { target_burn_id: selectedBurn.id });
      if (finalizeError) throw finalizeError;
      try {
        const result = await queueBurnNotifications(finalized, selectedMemberIds, 'prescribed_burn_selected', { sms: sendOperationalSms, stamp: finalized.roster_finalized_at || finalized.updated_at });
        setMessage(`Roster finalized with ${selectedMemberIds.length} selected member${selectedMemberIds.length === 1 ? '' : 's'}.`);
        if (result.smsSkipped) setWarning(`${result.smsSkipped} selected member${result.smsSkipped === 1 ? '' : 's'} did not opt in to operational SMS and were not texted.`);
      } catch (notifyError) {
        setMessage(`Roster finalized with ${selectedMemberIds.length} selected member${selectedMemberIds.length === 1 ? '' : 's'}.`);
        setWarning(`The roster is saved, but notification processing needs attention: ${notifyError?.message || 'notification dispatch failed.'}`);
      }
      await load();
      setSelectedBurnId(selectedBurn.id);
    } catch (finalizeError) {
      setError(finalizeError?.message || 'Unable to finalize the roster.');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const activeRoster = useMemo(() => selectedSignups.filter(item => ['selected','deployed'].includes(item.status)), [selectedSignups]);
  const standbyRoster = useMemo(() => selectedSignups.filter(item => item.status === 'standby'), [selectedSignups]);
  const rosterByPosition = useMemo(() => {
    const groups = new Map();
    activeRoster.forEach(signup => {
      const position = signup.selected_position || 'Unassigned';
      const list = groups.get(position) || [];
      list.push(signup);
      groups.set(position, list);
    });
    return [...groups.entries()];
  }, [activeRoster]);

  if (!profile?.is_admin) return <div className="admin-root"><main className="admin-page"><section className="admin-card admin-denied"><h1>Admin access required</h1></section></main></div>;

  return (
    <div className="admin-root">
      <header className="admin-topbar"><div><h1>Prescribed Burns</h1><p>Create opportunities, collect volunteers, build/finalize rosters, notify members, and preserve assignment history</p></div><div className="admin-topbar-actions"><button type="button" onClick={() => window.location.assign('/admin')}>Admin Home</button><button type="button" onClick={() => window.location.assign('/prescribed-burns')}>Member View</button></div></header>
      <main className="admin-page">
        <section className="admin-card">
          <div className="admin-section-head"><div><h2>{editingBurnId ? 'Edit Prescribed Burn' : 'Create Prescribed Burn'}</h2><p>REMS is excluded. Lifecycle changes happen with explicit action buttons below — no status dropdowns.</p></div>{editingBurnId && <button type="button" onClick={resetEditor}>Cancel Edit</button>}</div>
          <div className="v12-admin-form">
            <label>Burn / event name<input value={burnForm.name} onChange={event => setBurnForm(value => ({ ...value, name: event.target.value }))} /></label>
            <div className="v12-admin-grid"><label>Date<input type="date" value={burnForm.burnDate} onChange={event => setBurnForm(value => ({ ...value, burnDate: event.target.value }))} /></label><label>Start time<input type="time" value={burnForm.startTime} onChange={event => setBurnForm(value => ({ ...value, startTime: event.target.value }))} /></label><label>End time <small>optional</small><input type="time" value={burnForm.endTime} onChange={event => setBurnForm(value => ({ ...value, endTime: event.target.value }))} /></label><label>Location<input value={burnForm.location} onChange={event => setBurnForm(value => ({ ...value, location: event.target.value }))} /></label></div>
            <label>Meeting / staging instructions<textarea value={burnForm.staging} onChange={event => setBurnForm(value => ({ ...value, staging: event.target.value }))} /></label>
            <label>Notes / details<textarea value={burnForm.notes} onChange={event => setBurnForm(value => ({ ...value, notes: event.target.value }))} /></label>
            <div><strong>Staffing needs</strong>{requirementsForm.map((item,index) => <div className="v12-requirement-row" key={index}><label>Position<input value={item.position} onChange={event => updateRequirement(index,'position',event.target.value)} /></label><label>Needed<input type="number" min="1" max="100" value={item.quantity} onChange={event => updateRequirement(index,'quantity',event.target.value)} /></label><button type="button" onClick={() => setRequirementsForm(current => current.length === 1 ? current : current.filter((_,i) => i !== index))}>Remove</button></div>)}<button type="button" onClick={() => setRequirementsForm(current => [...current,{ position: '', quantity: 1 }])}>+ Add Position</button></div>
            <label><input type="checkbox" checked={sendOperationalSms} onChange={event => setSendOperationalSms(event.target.checked)} /> Also send optional operational SMS to opted-in eligible members when this action generates a notification</label>
            <div className="v12-check-row">
              <button type="button" className="v12-primary" disabled={saving} onClick={() => saveBurn(false)}>{saving ? 'Saving…' : editingBurnId ? 'Save Changes' : 'Save Draft'}</button>
              {!editingBurnId && <button type="button" disabled={saving} onClick={() => saveBurn(true)}>Save Draft + Open Signup</button>}
            </div>
          </div>
          {message && <div className="v12-success">{message}</div>}{warning && <div className="v12-admin-note" style={{ marginTop: 10 }}>{warning}</div>}{error && <div className="v12-error">{error}</div>}
        </section>

        <section className="admin-card">
          <div className="admin-section-head"><div><h2>Burns</h2><p>The current state is shown first. Only valid next actions are available.</p></div><button type="button" onClick={load}>Refresh</button></div>
          {status === 'loading' && <div className="admin-empty">Loading…</div>}
          {burns.map(burn => <div className="v12-admin-list-row" key={burn.id}><div><strong>{burn.name}</strong><small>{burn.burn_date}{burn.start_time ? ` · ${String(burn.start_time).slice(0,5)}` : ''} · {burn.location || 'No location entered'}</small><span><strong>{statusLabel(burn.status)}</strong> · {signups.filter(item => item.burn_id === burn.id && !['withdrawn','not_selected'].includes(item.status)).length} active volunteer/assignment record(s)</span></div><div><button type="button" onClick={() => setSelectedBurnId(burn.id)}>Manage</button>{!['completed','cancelled','archived','roster_selected'].includes(burn.status) && <button type="button" onClick={() => startEdit(burn)}>Edit Details</button>}{burn.status === 'draft' && <button type="button" className="v12-primary" onClick={() => transitionBurn(burn,'open_signup')}>Open Signup</button>}{burn.status === 'open_for_signup' && <button type="button" className="v12-primary" onClick={() => transitionBurn(burn,'close_signup')}>Close Signup</button>}{burn.status === 'roster_selected' && <button type="button" className="v12-primary" onClick={() => transitionBurn(burn,'mark_completed')}>Mark Completed</button>}{['draft','open_for_signup','signup_closed','roster_selected'].includes(burn.status) && <button type="button" onClick={() => transitionBurn(burn,'cancel')}>Cancel Burn</button>}{['completed','cancelled'].includes(burn.status) && <button type="button" onClick={() => transitionBurn(burn,'archive')}>Archive</button>}</div></div>)}
          {status === 'ready' && !burns.length && <div className="admin-empty">No prescribed burns yet.</div>}
        </section>

        {selectedBurn && <section className="admin-card">
          <div className="admin-section-head"><div><h2>{selectedBurn.name} — Roster</h2><p>{selectedRequirements.map(item => `${item.quantity} × ${item.position}`).join(' · ') || 'No staffing requirements'}</p></div><span className="admin-status">{statusLabel(selectedBurn.status)}</span></div>

          {selectedBurn.status === 'open_for_signup' && <div className="v12-admin-note"><strong>Signup is open.</strong><br />{selectedSignups.filter(item => item.status === 'signed_up').length} member(s) are currently volunteered. Close Signup before selecting the operational roster.</div>}

          {selectedBurn.status === 'signup_closed' && <>
            <div className="v12-admin-note" style={{ marginBottom: 12 }}><strong>Build roster</strong><br />Check Select for each member you want staffed, then assign that member a position. Use Standby for alternates. Everyone else will be retained as Not Selected when you save.</div>
            <div className="v12-admin-grid" style={{ marginBottom: 12 }}>
              {selectedRequirements.map(requirement => {
                const count = staffingCounts.get(requirement.position) || 0;
                return <div key={requirement.id} className="v12-admin-note"><strong>{requirement.position}</strong><br />{count}/{requirement.quantity} selected{count > requirement.quantity ? ' · over target' : ''}</div>;
              })}
            </div>
            <div className="v12-signup-grid">
              {candidateSignups.map(signup => {
                const member = memberFor(signup.member_id);
                const draft = rosterDraft[signup.id] || { selected: false, standby: false, position: '' };
                return <div className="v12-signup-row" key={signup.id} style={{ alignItems: 'center' }}>
                  <div><strong>{member?.full_name || member?.email || signup.member_id}</strong><small>Volunteer status: {statusLabel(signup.status)} · Signed up {when(signup.signed_up_at)}</small></div>
                  <label><input type="checkbox" checked={draft.selected} onChange={event => setRosterChoice(signup.id,{ selected: event.target.checked })} /> Select</label>
                  <select value={draft.position} disabled={!draft.selected} onChange={event => setRosterChoice(signup.id,{ position: event.target.value })}><option value="">Choose position…</option>{selectedRequirements.map(item => <option key={item.id} value={item.position}>{item.position}</option>)}</select>
                  <label><input type="checkbox" checked={draft.standby} disabled={draft.selected} onChange={event => setRosterChoice(signup.id,{ standby: event.target.checked })} /> Standby</label>
                  {draft.selected && !draft.position && <small style={{ color: 'var(--red)' }}>Position required before save/finalize.</small>}
                </div>;
              })}
              {!candidateSignups.length && <div className="admin-empty">No active volunteers are available to roster.</div>}
            </div>
            <div className="v12-check-row" style={{ marginTop: 14 }}><button type="button" disabled={saving} onClick={saveRoster}>Save Roster</button><button type="button" className="v12-primary" disabled={saving} onClick={finalizeRoster}>Finalize Roster + Notify Selected</button></div>
          </>}

          {['roster_selected','completed','cancelled','archived'].includes(selectedBurn.status) && <>
            <div className="v12-admin-note" style={{ marginBottom: 12 }}><strong>{selectedBurn.status === 'roster_selected' ? 'Day-of staffed roster' : 'Final roster history'}</strong><br />Primary staffing is separated from standby so the operational assignment is easy to read.</div>
            {rosterByPosition.map(([position, rows]) => <div key={position} style={{ marginBottom: 14 }}><h3>{position}</h3>{rows.map(signup => <div className="admin-table-row" key={signup.id}><span><strong>{memberFor(signup.member_id)?.full_name || signup.member_id}</strong><small>{statusLabel(signup.status)}</small></span><span>{signup.selected_position || position}</span></div>)}</div>)}
            {!activeRoster.length && <div className="admin-empty">No currently assigned members in the staffed roster.</div>}
            <h3 style={{ marginTop: 18 }}>Standby</h3>
            {standbyRoster.map(signup => <div className="admin-table-row" key={signup.id}><span><strong>{memberFor(signup.member_id)?.full_name || signup.member_id}</strong><small>Standby</small></span></div>)}
            {!standbyRoster.length && <div className="admin-empty">No standby members.</div>}
          </>}

          {selectedHistory.length > 0 && <details style={{marginTop:16}}><summary>Operational history ({selectedHistory.length})</summary>{selectedHistory.map(item => <div className="admin-table-row" key={item.id}><span><strong>{statusLabel(item.event_type)}</strong><small>{when(item.created_at)}</small></span>{item.member_id && <small>{memberFor(item.member_id)?.full_name || item.member_id}</small>}</div>)}</details>}
        </section>}
      </main>
    </div>
  );
}
